// =============================================================================
// celestial-foil — w11 "Yulduz" maximalist celestial wedding variant.
//
// Dilnoza & Bekzod · 21.11.2026 · Hyatt Regency, Tashkent.
//
// Visual language: a vintage celestial map. The HERO is where the maximalism
// lives — a midnight-indigo sky absolutely crowded with hand-placed gold-foil
// stars (4-pointed, round, haloed) and art-deco sun-burst rays radiating
// behind the names. A central constellation spells the initial "D" connected
// by hairlines.
//
// Signature interactions (hero only):
//   * Stars TWINKLE — random subset fades 0.6→1→0.6 on staggered intervals.
//   * Constellation lines DRAW on scroll — stroke-dashoffset animates from
//     full length to 0 once the SVG enters the viewport.
//   * `prefers-reduced-motion` short-circuits both: stars stay at full
//     opacity, lines stay drawn from first paint.
//
// Three `useSound` triggers: chime on RSVP yes, swoosh when the constellation
// finishes drawing, tap on each schedule row. A floating ambient toggle plays
// a cool airy A-minor pad while it's on.
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { VariantProps } from "./types";
import { useSound } from "@/lib/sound";
import { useAmbient, useReducedMotion } from "./_ambient";
import { MusicToggle } from "@/components/MusicToggle";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";

// ─── Ambient frequency spec — cool airy A-minor pad ────────────────────────
const CELESTIAL_AMBIENT = {
  freqs: [110.0, 164.81, 246.94, 329.63],
  type: "sine" as const,
  master: 0.025,
  lfo: 0.09,
};

// ─── Reveal on intersection ────────────────────────────────────────────────
function Reveal({ children, reduce }: { children: React.ReactNode; reduce: boolean }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState(reduce);
  useEffect(() => {
    if (reduce) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.16, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce]);
  return (
    <div
      ref={ref}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(20px)",
        transition: reduce ? "none" : "opacity .9s ease-out, transform .9s ease-out",
      }}
    >
      {children}
    </div>
  );
}

// ─── Star data — pre-baked positions for stable SSR ────────────────────────

interface Star {
  x: number;
  y: number;
  s: number;
  kind: 0 | 1 | 2; // 0=4-pointed, 1=round, 2=haloed
  delay: number;   // seconds — for the twinkle keyframe
  duration: number; // 4–8s
}

function makeStars(count: number, seed = 42): Star[] {
  let s = seed;
  const rand = () => ((s = (s * 9301 + 49297) % 233280) / 233280);
  const out: Star[] = [];
  for (let i = 0; i < count; i++) {
    out.push({
      x: rand() * 100,
      y: rand() * 100,
      s: 0.4 + rand() * 1.8,
      kind: (Math.floor(rand() * 3) as 0 | 1 | 2),
      delay: rand() * 6,
      duration: 4 + rand() * 4,
    });
  }
  return out;
}

// ─── "D" constellation — points roughly forming the letter D, plus links ───

const DLetter: { points: Array<[number, number]>; links: Array<[number, number]> } = {
  points: [
    [30, 12], [30, 32], [30, 52], [30, 72], [30, 88],
    [50, 88], [66, 80], [74, 64], [76, 50], [74, 36], [66, 20], [50, 12],
  ],
  links: [
    [0, 1], [1, 2], [2, 3], [3, 4],
    [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 11], [11, 0],
  ],
};

// ═════════════════════════════════════════════════════════════════════════════

export function CelestialFoilVariant({ t, isDark, ctx }: VariantProps) {
  const reduce = useReducedMotion();
  const { play } = useSound();

  // Hero stars — generated once, kept stable across renders.
  const heroStars = useMemo(() => makeStars(220, 7), []);

  // ─── Constellation draw-on-scroll ────────────────────────────────────────
  const constellationRef = useRef<SVGSVGElement | null>(null);
  const [drawn, setDrawn] = useState(reduce);

  useEffect(() => {
    if (reduce) {
      setDrawn(true);
      return;
    }
    if (typeof window === "undefined" || !constellationRef.current) return;
    const el = constellationRef.current;
    if (!("IntersectionObserver" in window)) {
      setDrawn(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setDrawn(true);
            // Sound trigger — swoosh as constellation completes.
            play("swoosh");
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduce, play]);

  // ─── RSVP wrapper — chime on first "yes" ────────────────────────────────
  const wrappedRsvp = {
    ...ctx.rsvp,
    setRsvp: (v: string | null) => {
      const before = ctx.rsvp.rsvp;
      ctx.rsvp.setRsvp(v);
      if (v === "yes" && before !== "yes") play("chime");
    },
  };

  // ─── Schedule taps ──────────────────────────────────────────────────────
  const onScheduleClickCapture = useCallback(() => {
    play("tap");
  }, [play]);

  // ─── Ambient music toggle ───────────────────────────────────────────────
  const [music, setMusic] = useState(false);
  useAmbient(music, CELESTIAL_AMBIENT);

  // ─── Couple names ──────────────────────────────────────────────────────
  // The editor saves a single `t.names` string ("A & B" / "A va B"). The hero
  // shows the two names stacked with an ampersand between them, so split on the
  // common separators. A single-name string falls back to one line.
  const coupleNames = useMemo(() => {
    const raw = (t.names || "").trim();
    const parts = raw.split(/\s*(?:&|·|\bva\b)\s*/i).filter(Boolean);
    return { first: parts[0] || raw, second: parts[1] || "" };
  }, [t.names]);

  // ─── Address line for map ──────────────────────────────────────────────
  const addressLine = t.address && t.address.trim() ? t.address : `${t.venue}, Toshkent`;

  // ─── Constellation polyline length (used for stroke-dasharray) ─────────
  const constellationLineLength = useMemo(() => {
    let total = 0;
    for (const [a, b] of DLetter.links) {
      const [ax, ay] = DLetter.points[a];
      const [bx, by] = DLetter.points[b];
      total += Math.hypot(bx - ax, by - ay);
    }
    return Math.ceil(total);
  }, []);

  return (
    <div className="v-w11">
      {/* ─── Local CSS — twinkle + draw + responsive scaffolding ─────── */}
      <style>{`
        @keyframes yulTwinkle {
          0%, 100% { opacity: 0.6; }
          50%      { opacity: 1; }
        }
        @keyframes yulDraw {
          to { stroke-dashoffset: 0; }
        }
        .yul-star-tw {
          animation: yulTwinkle var(--yul-dur, 6s) ease-in-out infinite;
          animation-delay: var(--yul-delay, 0s);
        }
        .yul-line {
          stroke-dasharray: var(--yul-len, 1000);
          stroke-dashoffset: var(--yul-len, 1000);
        }
        .yul-line.is-drawn {
          animation: yulDraw 2.4s ease-out forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .yul-star-tw { animation: none !important; opacity: 1 !important; }
          .yul-line { stroke-dashoffset: 0 !important; animation: none !important; }
        }
        .yul-name { font-size: clamp(56px, 13vw, 168px); }
        @media (max-width: 768px) {
          .v-w11-hero { padding: 64px 18px 48px !important; min-height: auto !important; }
          .v-w11-hero .yul-name { font-size: clamp(36px, 12vw, 72px) !important; }
          .v-w11-music-btn { min-height: 44px; min-width: 44px; }
          /* Date/location line: reduce letter-spacing and allow wrapping */
          .v-w11-hero .yul-date-line {
            font-size: 11px !important;
            letter-spacing: 0.18em !important;
            white-space: normal !important;
            word-break: break-word !important;
            max-width: 320px !important;
            margin: 0 auto !important;
          }
          /* Constellation SVG: shrink so it doesn't overlap the name block */
          .v-w11-hero .yul-constellation {
            width: min(52vw, 260px) !important;
            height: min(52vw, 260px) !important;
          }
          /* Horizontal rule under name: cap to viewport */
          .v-w11-hero .yul-rule { width: min(180px, 60vw) !important; }
        }
        .v-w11 button:focus-visible { outline: 2px solid ${t.accent}; outline-offset: 2px; }
      `}</style>

      {/* ════════════════════ HERO ════════════════════ */}
      <section
        className="v-w11-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          background: t.bg,
          color: t.ink,
          overflow: "hidden",
          display: "grid",
          placeItems: "center",
          padding: "120px 24px 80px",
        }}
      >
        {/* deep-space radial wash */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse at 50% 35%, ${t.accent}26 0%, transparent 65%)`,
            pointerEvents: "none",
          }}
        />

        {/* GIANT sun-burst behind the names */}
        <svg
          viewBox="-100 -100 200 200"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(120vw, 1100px)",
            height: "min(120vw, 1100px)",
            pointerEvents: "none",
          }}
        >
          <g opacity={0.6}>
            {Array.from({ length: 96 }, (_, i) => {
              const ang = (i / 96) * Math.PI * 2;
              const inner = 40;
              const outer = i % 2 === 0 ? 95 : 70;
              return (
                <line
                  key={i}
                  x1={Math.cos(ang) * inner}
                  y1={Math.sin(ang) * inner}
                  x2={Math.cos(ang) * outer}
                  y2={Math.sin(ang) * outer}
                  stroke={t.accent}
                  strokeWidth={0.3}
                />
              );
            })}
            <circle cx={0} cy={0} r={36} fill="none" stroke={t.accent} strokeWidth={0.5} />
            <circle cx={0} cy={0} r={28} fill="none" stroke={t.accent} strokeWidth={0.3} opacity={0.7} />
          </g>
        </svg>

        {/* Hundreds of stars */}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
        >
          {heroStars.map((st, i) => {
            const isTw = i % 4 === 0;
            const styleVars = {
              "--yul-dur": `${st.duration}s`,
              "--yul-delay": `${st.delay}s`,
            } as React.CSSProperties;
            const cls = isTw ? "yul-star-tw" : undefined;
            if (st.kind === 0) {
              const r = st.s;
              return (
                <path
                  key={i}
                  className={cls}
                  style={styleVars}
                  d={`M${st.x} ${st.y - r} L${st.x + r * 0.28} ${st.y - r * 0.28} L${st.x + r} ${st.y} L${st.x + r * 0.28} ${st.y + r * 0.28} L${st.x} ${st.y + r} L${st.x - r * 0.28} ${st.y + r * 0.28} L${st.x - r} ${st.y} L${st.x - r * 0.28} ${st.y - r * 0.28} Z`}
                  fill={t.accent}
                  opacity={0.85}
                />
              );
            }
            if (st.kind === 2) {
              return (
                <g key={i} className={cls} style={styleVars} opacity={0.9}>
                  <circle cx={st.x} cy={st.y} r={st.s * 0.35} fill={t.ink} />
                  <circle cx={st.x} cy={st.y} r={st.s * 0.9} fill="none" stroke={t.accent} strokeWidth={0.12} />
                </g>
              );
            }
            return (
              <circle
                key={i}
                className={cls}
                style={styleVars}
                cx={st.x}
                cy={st.y}
                r={st.s * 0.32}
                fill={t.ink}
                opacity={0.7}
              />
            );
          })}
        </svg>

        {/* Constellation forming a "D" between/behind the names */}
        <svg
          ref={constellationRef}
          className="yul-constellation"
          viewBox="0 0 100 100"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(70vw, 520px)",
            height: "min(70vw, 520px)",
            pointerEvents: "none",
            opacity: 0.85,
          }}
        >
          {DLetter.links.map(([a, b], i) => {
            const [ax, ay] = DLetter.points[a];
            const [bx, by] = DLetter.points[b];
            return (
              <line
                key={i}
                className={`yul-line${drawn ? " is-drawn" : ""}`}
                style={{ ["--yul-len" as string]: constellationLineLength }}
                x1={ax}
                y1={ay}
                x2={bx}
                y2={by}
                stroke={t.accent}
                strokeWidth={0.35}
                strokeLinecap="round"
              />
            );
          })}
          {DLetter.points.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={0.9} fill={t.accent} />
          ))}
        </svg>

        {/* Hero text */}
        <div style={{ position: "relative", zIndex: 2, textAlign: "center", maxWidth: 980 }}>
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.5em",
              color: t.accent,
              marginBottom: 32,
            }}
          >
            ✦ YULDUZLAR OSTIDA ✦
          </div>
          <h1
            className="yul-name"
            style={{
              fontFamily: t.font,
              fontStyle: "italic",
              fontWeight: 400,
              lineHeight: 0.95,
              color: t.accent,
              margin: 0,
              letterSpacing: "0.005em",
              textShadow: `0 0 32px ${t.accent}33`,
            }}
          >
            {coupleNames.first}
            {coupleNames.second && (
              <span style={{ display: "block", fontSize: "0.5em", color: t.ink, fontStyle: "italic", margin: "12px 0", opacity: 0.85 }}>&</span>
            )}
            {coupleNames.second}
          </h1>
          <div
            className="yul-rule"
            style={{
              width: 240,
              height: 1,
              background: t.accent,
              margin: "32px auto",
              opacity: 0.7,
            }}
          />
          <div
            className="yul-date-line"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: "0.4em",
              color: t.ink,
            }}
          >
            ♏  {t.date}  ·  {t.venue.toUpperCase()}  ♌
          </div>
          <div
            style={{
              marginTop: 24,
              fontFamily: t.font,
              fontStyle: "italic",
              fontSize: 18,
              color: `${t.ink}cc`,
            }}
          >
            «Yulduzlar bizning sevgimiz uchun guvoh bo'ladi»
          </div>
        </div>
      </section>

      {/* ════════════════════ COUNTDOWN ════════════════════ */}
      <Reveal reduce={reduce}>
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </Reveal>

      {/* ════════════════════ STORY (trimmed) ════════════════════ */}
      <Reveal reduce={reduce}>
        <StorySection
          t={t}
          eyebrow="Bizning hikoyamiz"
          title="Yulduzlar bizni bog'lagan kun"
          body={
            t.story && t.story.trim()
              ? t.story
              : "Dilnoza va Bekzod Toshkent planetariysida — Persei meteor yomg'iri kechasida tanishishgan. Bekzod bahor oqshomi Charvak suvi yoqasida, meteor yomg'iri ostida turmush qurish taklifini qildi. Endi esa 21-noyabr kuni sizni o'zlarining ulug' kechalariga taklif qilishadi."
          }
        />
      </Reveal>

      {/* ════════════════════ GALLERY ════════════════════ */}
      <Reveal reduce={reduce}>
        <GallerySection t={t} />
      </Reveal>

      {/* ════════════════════ SCHEDULE ════════════════════ */}
      <Reveal reduce={reduce}>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
        <div onClickCapture={onScheduleClickCapture}>
          <ScheduleSection
            t={t}
            items={[
              ["17:30", "Mehmonlarni kutib olish"],
              ["18:00", "Nikoh marosimi"],
              ["19:00", "Sherbat va tabriklar"],
              ["19:30", "Oqshom oshi"],
              ["21:00", "Raqs va musiqa"],
              ["22:30", "Oltin tort marosimi"],
              ["00:00", "Mehmonlarni kuzatish"],
            ]}
          />
        </div>
      </Reveal>

      {/* ════════════════════ MAP ════════════════════ */}
      <Reveal reduce={reduce}>
        <MapSection t={t} isDark={isDark} addressLine={addressLine} />
      </Reveal>

      {/* ════════════════════ RSVP ════════════════════ */}
      <Reveal reduce={reduce}>
        <RsvpSection
          t={t}
          isDark={isDark}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={wrappedRsvp.setRsvp}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          slug={ctx.slug}
          label="Yulduzli kechamizda biz bilan bo'ling"
        />
      </Reveal>

      {/* ════════════════════ SHARE ════════════════════ */}
      <Reveal reduce={reduce}>
        <ShareSection t={t} />
      </Reveal>

      {/* ════════════════════ FOOTER ════════════════════ */}
      <GuestFooter t={t} />

      {/* ─── Floating ambient toggle ───────────────────────────── */}
      <MusicToggle
        on={music}
        onToggle={() => setMusic((m) => !m)}
        accent={t.accent}
        ink={t.ink}
        style={{ bottom: 20, right: 20 }}
        className="v-w11-music-btn"
      />
    </div>
  );
}
